import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export interface DirectoryEntry {
  firstName: string;
  institution: string | null;
  course: string | null;
  year: string | null;
  joined: string;
}

/** Public: only students who opted in; only first name, institution, course, year. */
export const getStudentDirectory = createServerFn({ method: "GET" }).handler(
  async (): Promise<DirectoryEntry[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("profiles")
      .select("preferred_name, display_name, institution_name, course, year_of_study, created_at")
      .eq("directory_opt_in", true)
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error("Couldn't load the directory");
    return (data ?? []).map((p) => ({
      firstName: ((p.preferred_name || p.display_name || "Student").trim().split(/\s+/)[0]) || "Student",
      institution: p.institution_name,
      course: p.course,
      year: p.year_of_study,
      joined: p.created_at,
    }));
  },
);

export const getMyDirectoryOptIn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("profiles")
      .select("directory_opt_in, course, year_of_study")
      .eq("id", context.userId)
      .maybeSingle();
    return {
      optIn: !!data?.directory_opt_in,
      course: data?.course ?? "",
      year: data?.year_of_study ?? "",
    };
  });

export const setMyDirectoryListing = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        optIn: z.boolean(),
        course: z.string().trim().max(120),
        year: z.string().trim().max(40),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("profiles")
      .update({
        directory_opt_in: data.optIn,
        course: data.course || null,
        year_of_study: data.year || null,
      })
      .eq("id", context.userId);
    if (error) throw new Error("Couldn't save your listing");
    return { ok: true };
  });
